// Acting on a number, from the inspector. Two things a person can do with a number that needs attention:
//   Raise an issue        someone looks into it; it shows in their work and can become a CAPA
//   Start an improvement  a measured project with a target, in the Implementation Factory
// Either way the record carries the number, why it moved and the filters behind it, and shows the number live, so
// whoever opens it sees whether it has moved since.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, type DTile, type NumberRef, type XDataset, type XExplainRequest, type XExplanation } from "../api";
import { factory } from "../factory/factoryApi";
import { imp } from "../improve/impApi";
import { CATEGORIES, ErrorBox, recordHref } from "../ui";
import { fmtPlain, fmtRange, fmtSigned } from "./format";

type Kind = "issue" | "improve";
// The issue category a number from each dataset most often means; the person can change it.
const CATEGORY_FOR: Record<string, string> = {
  production: "quality_defect", material_usage: "quality_defect", issues: "quality_defect",
  stock: "material_shortage", open_orders: "material_shortage", receipts: "material_shortage", inventory: "material_shortage",
};
// The suite area an improvement belongs to, by the dataset's area.
const AREA_FOR: Record<string, string> = { Sales: "commercial", Contracts: "commercial", Inventory: "inventory", Production: "production", "Supply Chain": "supply", Finance: "finance", Quality: "quality", PLM: "engineering" };
const CMP = { py: "the prior year", pp: "the prior period" } as const;

export function ActPanel({ tile, what, ex, req, ds, dashboardId, dashboardName, onDone, onCancel }: {
  tile: DTile; what: string; ex: XExplanation; req: XExplainRequest; ds?: XDataset;
  dashboardId?: string; dashboardName?: string; onDone: (ref: NumberRef) => void; onCancel: () => void;
}) {
  const format = ex.value.format;
  const good = tile.view?.good === "down" ? "down" : "up";
  const bad = ex.change !== null && ex.change !== 0 && (ex.change > 0) !== (good === "up");
  const changeText = ex.then && ex.compare ? `${fmtSigned(ex.change, format)} against ${CMP[ex.compare]}` : null;
  const [kind, setKind] = useState<Kind>("issue");
  const [title, setTitle] = useState(`${what}: ${changeText ?? `at ${fmtPlain(ex.now.value, format)}`}`.slice(0, 200));
  const [category, setCategory] = useState(CATEGORY_FOR[req.dataset] ?? "other");
  const [severity, setSeverity] = useState(bad ? "high" : "medium");
  const [owner, setOwner] = useState("");
  // Issues and improvements link to the process they're in (the process catalog).
  const [proc, setProc] = useState("");
  const [procs, setProcs] = useState<{ number: string; name: string; area: string | null }[]>([]);
  useEffect(() => { imp.processes().then((c) => setProcs(c.processes.map((p) => ({ number: p.number, name: p.name, area: p.area })))).catch(() => setProcs([])); }, []);
  // An improvement's target starts at where the number was, when it got worse.
  const toUnit = (v: number | null | undefined) => (v === null || v === undefined ? null : format === "pct" ? Math.round(v * 1000) / 10 : format === "money" ? Math.round(v) : Math.round(v * 10) / 10);
  const [target, setTarget] = useState(bad && ex.then ? String(toUnit(ex.then.value) ?? "") : "");
  const [note, setNote] = useState("");
  const [people, setPeople] = useState<{ id: string; name: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [done, setDone] = useState<NumberRef | null>(null);
  // A record already made on an earlier try, so trying again only attaches the number instead of making a second one.
  const [made, setMade] = useState<string | null>(null);
  useEffect(() => { api.actPeople().then(setPeople).catch(() => setPeople([])); }, []);
  useEffect(() => { setTitle((t) => (kind === "improve" && t.startsWith(`${what}:`) ? `Improve ${ex.value.name}${what !== tile.title ? ` for ${what.split(" · ").slice(1).join(" · ")}` : ""}`.slice(0, 200) : t)); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps
  const unit = format === "pct" ? "%" : format === "money" ? "$" : "";

  // What goes into the record's text, so it reads on its own (and in search, chat and MCP).
  const story = useMemo(() => [
    `${dashboardName ? `From the ${dashboardName} dashboard` : "From a dashboard"}: ${what}${ex.now.from && ex.now.to ? `, ${fmtRange(ex.now.from, ex.now.to)}` : ""}. ${fmtPlain(ex.now.value, format)}${changeText ? `, ${changeText}` : ""}.`,
    ex.headline, ...ex.detail.map((d) => `- ${d}`), ...(note.trim() ? ["", note.trim()] : []),
  ].join("\n"), [dashboardName, what, ex, note, changeText, format]);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      let record: string;
      if (made) record = made;
      else if (kind === "issue") {
        const rec = await api.createIssue({ title: title.trim(), category, description: story, ...(proc ? { processId: proc } : {}) });
        setMade(rec.id);
        if (owner) await api.triage(rec.id, severity, owner);
        record = rec.id;
      } else {
        const t = target.trim() === "" ? null : Number(target);
        if (t !== null && !Number.isFinite(t)) throw new Error("The target must be a number");
        const r = await factory.create({ title: title.trim(), problem: story, measure: ex.value.name, unit, better: good === "up" ? "higher" : "lower",
          currentValue: toUnit(ex.now.value) as any, targetValue: t as any, area: AREA_FOR[ds?.area ?? ""] ?? null, projectType: "improvement", ...(proc ? { processId: proc } : {}), ...(owner ? { ownerId: owner } : {}) } as any);
        record = r.number;
      }
      setMade(record);
      const ref = await api.actKeep(record, { dashboardId, tileId: tile.id, title: what, request: req, good });
      setDone(ref); onDone(ref);
    } catch (e) { setErr(e); } finally { setBusy(false); }
  };

  if (done) {
    const who = people.find((p) => p.id === done.record.ownerId)?.name;
    return (
      <div className="na na-done">
        <p className="na-ok"><b>{done.record.number}</b> is {done.record.type === "issue" ? "raised" : "started"}.</p>
        <p className="muted">{done.record.type === "issue" ? (who ? `Assigned to ${who}, and in their work.` : "It waits for triage on the Issues page.") : `It's at Plan in the Implementation Factory${who ? `, owned by ${who}` : ""}.`} It carries this number and shows it live, so anyone who opens it sees whether it has moved.</p>
        <div className="na-btns">
          <Link className="btn primary" to={recordHref(done.record.type, done.record.number)}>Open {done.record.number}</Link>
          <button className="btn" onClick={onCancel}>Back to Why</button>
        </div>
      </div>
    );
  }
  return (
    <div className="na">
      <div className="na-kinds" role="radiogroup" aria-label="What to do">
        <button role="radio" aria-checked={kind === "issue"} className={kind === "issue" ? "on" : ""} disabled={!!made} onClick={() => setKind("issue")}>
          <b>Raise an issue</b><span>Someone looks into it. It shows in their work and can become a CAPA.</span>
        </button>
        <button role="radio" aria-checked={kind === "improve"} className={kind === "improve" ? "on" : ""} disabled={!!made} onClick={() => setKind("improve")}>
          <b>Start an improvement</b><span>Set a target and measure the result in the Implementation Factory.</span>
        </button>
      </div>
      <label className="na-f"><span>Title</span><input className="input" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} /></label>
      <label className="na-f"><span>Process{kind === "issue" && !owner ? <i className="muted"> optional until it's assigned</i> : null}</span>
        <select className="select" value={proc} onChange={(e) => setProc(e.target.value)}>
          <option value="">{kind === "issue" && !owner ? "Not sure yet" : "Choose the process…"}</option>
          {procs.map((p) => <option key={p.number} value={p.number}>{p.number} · {p.name}</option>)}
        </select></label>
      {kind === "issue" ? (<>
        <div className="na-row">
          <label className="na-f"><span>Category</span>
            <select className="select" value={category} onChange={(e) => setCategory(e.target.value)}>{CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}</select>
          </label>
          <label className="na-f"><span>Assign to</span>
            <select className="select" value={owner} onChange={(e) => setOwner(e.target.value)}>
              <option value="">Nobody yet</option>
              {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
        </div>
        {owner ? (
          <label className="na-f"><span>Severity</span>
            <select className="select" value={severity} onChange={(e) => setSeverity(e.target.value)}>
              {["low", "medium", "high", "critical"].map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
            </select>
          </label>
        ) : <p className="na-hint muted">Unassigned issues wait for triage on the Issues page.</p>}
      </>) : (<>
        <div className="na-row">
          <label className="na-f"><span>Measure</span><input className="input" value={`${ex.value.name}${unit ? ` (${unit})` : ""}`} readOnly /></label>
          <label className="na-f sm"><span>Now</span><input className="input" value={String(toUnit(ex.now.value) ?? "")} readOnly /></label>
          <label className="na-f sm"><span>Target</span><input className="input" inputMode="decimal" placeholder={good === "up" ? "Higher" : "Lower"} value={target} onChange={(e) => setTarget(e.target.value)} /></label>
        </div>
        <label className="na-f"><span>Owner</span>
          <select className="select" value={owner} onChange={(e) => setOwner(e.target.value)}>
            <option value="">Me</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
      </>)}
      <label className="na-f"><span>Note <i className="muted">optional</i></span><textarea className="input" rows={2} placeholder="What to look at first" value={note} onChange={(e) => setNote(e.target.value)} /></label>
      <div className="na-attach">
        <span className="na-attach-h">Goes with it</span>
        <p><b>{what}</b> · {fmtPlain(ex.now.value, format)}{changeText ? ` · ${changeText}` : ""}</p>
        <p className="muted">{ex.headline}</p>
        <p className="muted">The filters behind the number, and the number live on the record.</p>
      </div>
      <ErrorBox error={err} />
      <div className="na-btns">
        <button className="btn primary" disabled={busy || title.trim().length < 3 || (!proc && (kind !== "issue" || !!owner))} title={!proc && (kind !== "issue" || owner) ? "Choose the process" : undefined} onClick={submit}>{busy ? "Saving…" : made ? "Try Again" : kind === "issue" ? "Raise Issue" : "Start Improvement"}</button>
        <button className="btn" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </div>
  );
}
